// Header.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link as ScrollLink } from "react-scroll";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Header.css";

const Header = ({ currentLanguage = "en", onLanguageSwitch }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  // Սա թույլ է տալիս, որ մենյուն փակվի, երբ օգտատերը սեղմում է էկրանի այլ հատվածում
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target)
      ) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside); // Ավելացնում ենք մոբայլ հպումների համար
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const switchLanguage = (lang) => {
    i18n.changeLanguage(lang);

    // Թարմացնում ենք URL-ը նոր ընտրված լեզվով որպես query պարամետր
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("lang", lang);
    navigate(`${location.pathname}?${searchParams.toString()}`);

    if (onLanguageSwitch) onLanguageSwitch(lang);
  };

  const languages = [
    { code: "en", label: "EN" },
    { code: "hy", label: "HY" },
    { code: "ru", label: "RU" },
    { code: "el", label: "CY" },
  ];

  return (
    <header
      className={`main-header ${mobileMenuOpen ? "mobile-menu-open" : ""}`}
    >
      <div className="logo-container">
        <NavLink to={`/?lang=${currentLang}`} onClick={closeMobileMenu}>
          <img src="/Logo.png" alt="Logo" className="logo" />
        </NavLink>
      </div>

      <nav
        className={`main-navigation ${mobileMenuOpen ? "mobile-menu-open" : ""}`}
      >
        <ul className={`${mobileMenuOpen ? "open" : ""}`}>
          <li>
            <ScrollLink
              to="products"
              smooth={true}
              duration={500}
              spy={true}
              activeClass="active-bg"
              onClick={() => {
                closeMobileMenu();
                navigate(`/?lang=${currentLang}`);
              }}
            >
              {t("products")}
            </ScrollLink>
          </li>
          <li>
            <ScrollLink
              to="about"
              smooth={true}
              duration={500}
              spy={true}
              activeClass="active-bg"
              onClick={() => {
                closeMobileMenu();
                navigate(`/?lang=${currentLang}`);
              }}
            >
              {t("about")}
            </ScrollLink>
          </li>
          <li>
            <ScrollLink
              to="resources"
              smooth={true}
              duration={500}
              spy={true}
              activeClass="active-bg"
              onClick={() => {
                closeMobileMenu();
                navigate(`/?lang=${currentLang}`);
              }}
            >
              {t("resources")}
            </ScrollLink>
          </li>

          <li className="dropdown">
            <ScrollLink
              to="features"
              smooth={true}
              duration={500}
              spy={true}
              activeClass="active-bg"
              onClick={() => {
                closeMobileMenu();
                navigate(`/?lang=${currentLang}`);
              }}
            >
              {t("featuresHeader")}
            </ScrollLink>
            <div className="dropdown-content">{/* dropdown items here */}</div>
          </li>

          {/* <li>
            <NavLink
              to={`/packages?lang=${currentLang}`}
              className={({ isActive }) => (isActive ? 'active-bg-nav' : '')}
              onClick={closeMobileMenu}
            >
              {t('packages')}
            </NavLink>
          </li> */}
          <li>
            <NavLink
              to={`/services?lang=${currentLang}`}
              className={({ isActive }) => (isActive ? "active-bg-nav" : "")}
              onClick={closeMobileMenu}
            >
              {t("services_header")}
            </NavLink>
          </li>
          <li>
            <NavLink
              to={`/bio?lang=${currentLang}`}
              className={({ isActive }) => (isActive ? "active-bg-nav" : "")}
              onClick={closeMobileMenu}
            >
              {t("bio")}
            </NavLink>
          </li>

          {/* ✅ Ավելացնենք QR Menu */}
          <li>
            <NavLink
              to={`/qr-menu?lang=${currentLang}`}
              className={({ isActive }) => (isActive ? "active-bg-nav" : "")}
              onClick={closeMobileMenu}
            >
              QR Menu
            </NavLink>
          </li>
        </ul>
        
        <div className="language-dropdown" ref={langDropdownRef}>
            <button
              className="lang-btn-toggle"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLangDropdownOpen(!langDropdownOpen);
              }}
              aria-label="Select Language"
            >
              {languages.find((l) => l.code === currentLang)?.label || "EN"}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`lang-chevron ${langDropdownOpen ? "open" : ""}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>

            {langDropdownOpen && (
              <ul className="lang-menu">
                {languages.map((lang) => (
                  <li
                    key={lang.code}
                    className={currentLang === lang.code ? "active" : ""}
                    onClick={() => {
                      switchLanguage(lang.code);
                      setLangDropdownOpen(false);
                      closeMobileMenu();
                    }}
                  >
                    {lang.label}
                  </li>
                ))}
              </ul>
            )}
        </div>

        <div className="menu-icon" onClick={toggleMobileMenu}>
          <div className={`bar top ${mobileMenuOpen ? "open" : ""}`}></div>
          <div className={`bar middle ${mobileMenuOpen ? "open" : ""}`}></div>
          <div className={`bar bottom ${mobileMenuOpen ? "open" : ""}`}></div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
